import React, { useEffect, useRef, useState } from 'react';
import closeIcon from '../images/close-line.svg';
import bookieLogo from '../images/bookie-logo.png';
import { toPng } from 'html-to-image';
import './SharableCard.css';
import { useTranslation } from '../i18n';
import moment from 'moment';
import { formatTime, getSuperlative, propsToText } from '../utils/functions';
import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { analytics, db } from '../firebaseConfig';
import confetti from 'canvas-confetti';
import { logEvent } from 'firebase/analytics';

function SharableCard({
  handleReset,
  globalState,
  quote,
  summary,
  review,
  user,
}) {
  const ref = useRef();
  const lang = localStorage.getItem('language');
  const { t } = useTranslation();
  const { share } = globalState;

  const [book, setBook] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [isWiggling, setIsWiggling] = useState(false);
  const [shared, setShared] = useState(false);

  const handleWiggle = () => {
    // Temporarily remove the wiggle class by toggling the state
    setIsWiggling(false);
    setTimeout(() => {
      setIsWiggling(true);
    }, 10); // Reapply the wiggle class after 10ms
  };

  const throwConfetti = () => {
    confetti({
      particleCount: 170,
      spread: 70,
      origin: { y: 0.7 },
    });
    handleWiggle();
  };

  useEffect(() => {
    const getBook = async () => {
      try {
        const q = query(
          collection(db, 'UserBooks'),
          where('bookID', '==', share.bookID),
          where('userID', '==', user?.uid),
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const docData = doc.data();

          setBook(docData);
        } else {
          console.error('Book was not found.');
        }
      } catch (error) {
        console.error('Error getting UserBook: ', error);
      }
    };

    if (share.bookCompleted) {
      throwConfetti();

      getBook();
    }
  }, [share.bookID, user?.uid, share.bookCompleted, db]);

  const close = () => {
    setDisabled(true);
    handleReset(share.bookCompleted);
  };

  const handleShare = async () => {
    if (ref.current) {
      try {
        // Ensure fonts are fully loaded
        await document.fonts.ready;

        const dataUrl = await toPng(ref.current, {
          cacheBust: true,
          pixelRatio: 2, // Higher resolution
          style: {
            transform: 'scale(1)',
            transformOrigin: 'top left',
          },
        });

        const response = await fetch(dataUrl);
        const blob = await response.blob();

        if (
          navigator.canShare &&
          navigator.canShare({
            files: [
              new File([blob], 'bookie-share-card.png', { type: 'image/png' }),
            ],
          })
        ) {
          const file = new File([blob], 'bookie-share-card.png', {
            type: 'image/png',
          });
          await navigator.share({
            title: `${getSuperlative(share.currentStreak, t)}!`,
            text: `${user?.name} ${
              share.bookCompleted ? t.share.finishbook : t.share.finishsession
            } 🎉 https://bookie.website`,
            files: [file],
          });

          if (!shared) {
            await addDoc(collection(db, 'Shares'), {
              user: user?.uid,
              bookCompleted: share.bookCompleted,
              secondsRead: share.secondsRead,
              pagesRead: share.pagesRead,
              currentStreak: share.currentStreak,
            });
            setShared(true);
            logEvent(analytics, 'reading_session_share', {
              bookCompleted: share.bookCompleted,
              secondsRead: share.secondsRead,
              pagesRead: share.pagesRead,
              currentStreak: share.currentStreak,
            });
          }
        } else {
          alert(t.share.error);
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = 'bookie-share-card.png';
          link.click();
        }
      } catch (err) {
        console.error('Error generating or sharing the image:', err);
      }
    }
  };

  return (
    <div className="dialog-content fullscreen-overlay">
      <div
        className="fullscreen-container"
        tabIndex={-1}
        aria-label={t.close}
        aria-disabled={disabled}
      >
        <button
          className="fullscreen-close"
          onClick={close}
          disabled={disabled}
        >
          <img src={closeIcon} width={42} height={42} alt="Close icon" />
          {t.close}
        </button>

        <div className="sharable-card" ref={ref}>
          <div className="sharable-header">
            <img
              src={bookieLogo}
              alt="Logo"
              height={40}
              width={124}
              className="sharable-logo"
            />

            <div className="sharable-heading">
              <div className="fs-xl">{user?.name},</div>
              <div className="fs-l">
                {share.bookCompleted
                  ? t.share.finishbook
                  : t.share.finishsession}
              </div>
            </div>

            {share.bookCompleted ? (
              <div
                className={`fs-xxxl ${isWiggling ? 'wiggle' : ''}`}
                onClick={() => throwConfetti()}
              >
                🎉
              </div>
            ) : (
              <div className="sharable-time">
                {formatTime(share.secondsRead || 0)}
              </div>
            )}

            <div className="sharable-heading centered">
              <div className="fs-xl">{share.title}</div>
              <div className="fs-l">{`${t.by} ${propsToText(share.author_name, 1)}`}</div>
            </div>
          </div>

          <div className="sharable-body">
            {share.bookCompleted
              ? [
                  book?.dateStarted && (
                    <div
                      key="book-date-started"
                      className="sharable-session-info "
                    >
                      <div className="fs-l">
                        {book.dateFinished ? t.dateCompleted : t.dateStarted}
                      </div>
                      <div className="sharable-session-stat">
                        <div className="fs-m pt-65 nowrap">
                          {book.dateFinished
                            ? moment(book.dateStarted.toDate())
                                .locale(lang)
                                .format('D MMM') +
                              `—` +
                              moment(book.dateFinished.toDate())
                                .locale(lang)
                                .format('D MMM, YYYY')
                            : moment(book.dateStarted.toDate())
                                .locale(lang)
                                .format('D MMM, YYYY')}
                        </div>
                      </div>
                    </div>
                  ),
                  <div key="book-stat" className="sharable-session-stat">
                    <div className="sharable-session-block">
                      <div className="fs-m pt-65">{t.pagesRead}</div>
                      <div className="fs-xxl">
                        {book?.pages ? book.pages : 0}
                      </div>
                    </div>

                    <div className="sharable-session-block">
                      <div className="fs-m pt-65">{t.timeRead}</div>
                      <div className="fs-xxl">
                        {formatTime(book?.time ? book.time : 0)}
                      </div>
                    </div>

                    <div className="sharable-session-block">
                      <div className="fs-m pt-65">{t.share.sessions}</div>
                      <div className="sharable-session-block-item">
                        <div className="fs-xxl">
                          {book?.sessions ? book.sessions.length : 1}
                        </div>
                      </div>
                    </div>
                  </div>,
                ]
              : [
                  <div key="session-info" className="sharable-session-info ">
                    <div className="fs-l">{t.share.sessioninfo}</div>
                    <div className="fs-m pt-65">
                      {moment(share.date).locale(lang).format('D MMM, HH:mm')}
                    </div>
                  </div>,
                  <div key="session-stat" className="sharable-session-stat">
                    <div className="sharable-session-block">
                      <div className="fs-m pt-65">{t.pagesRead}</div>
                      <div className="fs-xxl">{share.pagesRead || 0}</div>
                    </div>

                    <div className="sharable-session-block">
                      <div className="fs-m pt-65">{t.timeRead}</div>
                      <div className="sharable-session-block-item">
                        <div className="fs-xl pt-65">
                          {!(share.secondsRead % 60) ? '' : '~'}
                        </div>
                        <div className="fs-xxl">
                          {Math.round(share.secondsRead / 60 || 0)}
                        </div>
                        <div className="fs-m pt-65">{t.min}</div>
                      </div>
                    </div>

                    <div className="sharable-session-block">
                      <div className="fs-m pt-65">{t.share.streak}</div>
                      <div className="sharable-session-block-item">
                        <div className="fs-xxl">{share.currentStreak || 1}</div>
                        <div className="fs-m pt-65">
                          {share.currentStreak === 1 ? t.d : t.ds}
                        </div>
                      </div>
                    </div>
                  </div>,
                ]}

            {quote && [
              <div key="qoute-separator" className="separator"></div>,
              <div key="qoute" className="sharable-session-block">
                <div className="fs-m pt-65">{t.share.quote}</div>
                <div>{quote}</div>
              </div>,
            ]}

            {summary && [
              <div key="summary-separator" className="separator"></div>,
              <div key="summary" className="sharable-session-block">
                <div className="fs-m pt-65">{t.summary}</div>
                <div>{summary}</div>
              </div>,
            ]}

            {share.bookCompleted &&
              review && [
                <div key="review-separator" className="separator"></div>,
                <div key="review" className="sharable-session-block">
                  <div className="fs-m pt-65">{t.review}</div>
                  <div>{review}</div>
                </div>,
              ]}
          </div>
        </div>

        <div className="btn-group-wrapper">
          <div className="btn-group btn-group-two-col">
            <button
              className="btn btn-secondary w-100"
              onClick={handleShare}
              disabled={disabled}
            >
              {t.share.share}
            </button>
            <button
              className="btn btn-primary w-100"
              type="button"
              onClick={close}
              disabled={disabled}
            >
              {t.close}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SharableCard;
